/**
 * Utility functions for generating consistent colors for users
 */

// Available color classes that match the EVENT_COLOR_OPTIONS format
const AVAILABLE_COLOR_CLASSES = [
	'bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200',
	'bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200',
	'bg-yellow-200 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200',
	'bg-purple-200 dark:bg-purple-800 text-purple-800 dark:text-purple-200',
	'bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200',
	'bg-indigo-200 dark:bg-indigo-800 text-indigo-800 dark:text-indigo-200',
	'bg-pink-200 dark:bg-pink-800 text-pink-800 dark:text-pink-200',
	'bg-gray-200 dark:bg-gray-800 text-gray-800 dark:text-gray-200',
	'bg-teal-200 dark:bg-teal-800 text-teal-800 dark:text-teal-200',
	'bg-orange-200 dark:bg-orange-900 text-orange-800 dark:text-orange-200',
	'bg-cyan-200 dark:bg-cyan-800 text-cyan-800 dark:text-cyan-200',
	'bg-lime-200 dark:bg-lime-800 text-lime-800 dark:text-lime-200',
	'bg-emerald-200 dark:bg-emerald-800 text-emerald-800 dark:text-emerald-200',
	'bg-violet-200 dark:bg-violet-800 text-violet-800 dark:text-violet-200',
	'bg-fuchsia-200 dark:bg-fuchsia-800 text-fuchsia-800 dark:text-fuchsia-200',
	'bg-rose-200 dark:bg-rose-800 text-rose-800 dark:text-rose-200',
	'bg-sky-200 dark:bg-sky-800 text-sky-800 dark:text-sky-200',
	'bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200',
	'bg-slate-200 dark:bg-slate-800 text-slate-800 dark:text-slate-200',
	'bg-zinc-200 dark:bg-zinc-800 text-zinc-800 dark:text-zinc-200',
] as const;

/**
 * Generate a consistent color class for a user based on their ID
 * Uses a simple hash function to ensure the same user always gets the same color
 */
export const generateUserColorClass = (userId: string): string => {
	// Simple hash function for consistent color assignment
	let hash = 0;
	for (let i = 0; i < userId.length; i++) {
		const char = userId.charCodeAt(i);
		hash = (hash << 5) - hash + char;
		hash = hash & hash; // Convert to 32-bit integer
	}

	// Get absolute value and map to available colors
	const colorIndex = Math.abs(hash) % AVAILABLE_COLOR_CLASSES.length;
	return AVAILABLE_COLOR_CLASSES[colorIndex];
};

/**
 * Get a user's display name, falling back to email if name is not available
 */
export const getUserDisplayName = (user: {
	name?: string;
	email?: string;
	username?: string;
}): string => {
	if (user.name && user.name.trim()) {
		return user.name.trim();
	}

	if (user.email && user.email.trim()) {
		return user.email.trim();
	}

	if (user.username && user.username.trim()) {
		return user.username.trim();
	}

	return 'Unknown User';
};

interface NamedUser {
	_id: string;
	name?: string;
	email?: string;
	username?: string;
}

/**
 * Display names keyed by user id. When two or more people share a name
 * (ignoring case and spacing), each gets their email in brackets so they
 * can be told apart, e.g. "Emily (emily@example.com)".
 */
export const getUniqueUserDisplayNames = (
	users: NamedUser[]
): Map<string, string> => {
	const nameKey = (user: NamedUser) =>
		getUserDisplayName(user).toLowerCase().replace(/\s+/g, ' ');
	const counts = new Map<string, number>();
	for (const user of users) {
		counts.set(nameKey(user), (counts.get(nameKey(user)) || 0) + 1);
	}

	return new Map(
		users.map((user) => {
			const name = getUserDisplayName(user);
			const email = (user.email || user.username || '').trim();
			const isShared = (counts.get(nameKey(user)) || 0) > 1;
			return [
				user._id,
				isShared && email && email !== name ? `${name} (${email})` : name,
			];
		})
	);
};

/**
 * Generate a short display name from a full name or email
 * Examples: "John Doe" -> "JD", "john@example.com" -> "john"
 */
export const getShortDisplayName = (displayName: string): string => {
	if (displayName.includes('@')) {
		// For emails, return the part before @
		return displayName.split('@')[0];
	}

	// For names, return initials
	const words = displayName.trim().split(/\s+/);
	if (words.length >= 2) {
		return words
			.slice(0, 2)
			.map((word) => word.charAt(0).toUpperCase())
			.join('');
	}

	// Single word, return first two characters
	return displayName.slice(0, 2).toUpperCase();
};
